import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import { LogOut, Dumbbell, Flame, Activity, Brain, Plus, Scale, Camera, Loader2, TrendingUp, TrendingDown, Target, Clock, Upload } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, BarChart, Bar } from 'recharts';

const ClientDashboard = () => {
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [advice, setAdvice] = useState(null);
  const [adviceLoading, setAdviceLoading] = useState(false);
  const [showCalForm, setShowCalForm] = useState(false);
  const [showWorkoutForm, setShowWorkoutForm] = useState(false);
  const [showWeightForm, setShowWeightForm] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [calForm, setCalForm] = useState({ date: new Date().toISOString().split('T')[0], total_calories: '', protein: '', carbs: '', fats: '', notes: '' });
  const [workoutForm, setWorkoutForm] = useState({ date: new Date().toISOString().split('T')[0], workout_type: 'gym', duration_minutes: '', description: '' });
  const [weightForm, setWeightForm] = useState({ date: new Date().toISOString().split('T')[0], weight: '', notes: '' });

  useEffect(() => {
    if (!user || user.role !== 'client') { navigate('/portal'); return; }
    loadDashboard();
  }, [user]);

  const loadDashboard = async () => {
    try {
      const res = await api.get('/api/client/dashboard-summary');
      setDash(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const submitCalories = async (e) => {
    e.preventDefault();
    await api.post('/api/client/calorie-log', { ...calForm, total_calories: parseInt(calForm.total_calories), protein: calForm.protein ? parseInt(calForm.protein) : null, carbs: calForm.carbs ? parseInt(calForm.carbs) : null, fats: calForm.fats ? parseInt(calForm.fats) : null });
    setShowCalForm(false);
    setCalForm({ date: new Date().toISOString().split('T')[0], total_calories: '', protein: '', carbs: '', fats: '', notes: '' });
    loadDashboard();
  };

  const submitWorkout = async (e) => {
    e.preventDefault();
    await api.post('/api/client/workout-log', { ...workoutForm, duration_minutes: parseInt(workoutForm.duration_minutes) });
    setShowWorkoutForm(false);
    setWorkoutForm({ date: new Date().toISOString().split('T')[0], workout_type: 'gym', duration_minutes: '', description: '' });
    loadDashboard();
  };

  const submitWeight = async (e) => {
    e.preventDefault();
    await api.post('/api/client/weight-log', { ...weightForm, weight: parseFloat(weightForm.weight) });
    setShowWeightForm(false);
    setWeightForm({ date: new Date().toISOString().split('T')[0], weight: '', notes: '' });
    loadDashboard();
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('notes', '');
    try {
      await api.post('/api/client/progress-photo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      loadDashboard();
    } catch (err) { console.error(err); }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getAdvice = async () => {
    setAdviceLoading(true);
    try {
      const res = await api.post('/api/client/ai-advice');
      setAdvice(res.data.advice);
    } catch (e) { setAdvice('Unable to get advice right now.'); }
    setAdviceLoading(false);
  };

  const handleLogout = () => { logout(); navigate('/portal'); };
  const goalLabels = { fat_loss: 'Fat Loss', muscle_gain: 'Muscle Gain', maintenance: 'Maintenance' };
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  if (loading) return <div className="portal-page"><div className="portal-loading" style={{paddingTop:'8rem'}}><Loader2 size={32} className="portal-spin" /> Loading dashboard...</div></div>;

  const p = dash?.profile || {};
  const weightData = (dash?.weight_logs || []).map(w => ({ date: w.date?.slice(5), weight: w.weight }));
  const calData = (dash?.calorie_logs || []).map(c => ({ date: c.date?.slice(5), cals: c.total_calories }));

  // Workouts per week calc
  const workoutsByWeek = {};
  (dash?.workout_logs || []).forEach(w => {
    const d = new Date(w.date);
    const weekStart = new Date(d);
    weekStart.setDate(d.getDate() - d.getDay() + 1);
    const key = weekStart.toISOString().slice(5, 10);
    workoutsByWeek[key] = (workoutsByWeek[key] || 0) + 1;
  });
  const workoutWeekData = Object.entries(workoutsByWeek).map(([wk, count]) => ({ week: wk, sessions: count })).slice(-8);

  return (
    <div className="portal-page" data-testid="client-dashboard">
      <div className="portal-topbar">
        <h2><Dumbbell size={20} /> My Dashboard</h2>
        <div className="portal-topbar-right">
          <span>Hi, {user?.name}</span>
          <button onClick={handleLogout} className="portal-btn-ghost" data-testid="client-logout-btn"><LogOut size={16} /> Logout</button>
        </div>
      </div>

      <div className="portal-content dash-content">

        {/* ===== Progress Photos Hero ===== */}
        {(dash?.first_photo || dash?.total_photos > 0) && (
          <div className="dash-card dash-card-wide dash-photos-hero" data-testid="progress-photos-hero">
            <h3 className="dash-card-title"><Camera size={18} /> Your Transformation</h3>
            <div className="dash-photo-compare">
              {dash.first_photo && (
                <div className="dash-photo-box">
                  <p className="dash-photo-label">Day 1</p>
                  <img src={`${API_BASE}${dash.first_photo.url}`} alt="First progress" />
                  <span className="dash-photo-date">{dash.first_photo.created_at?.slice(0, 10)}</span>
                </div>
              )}
              {dash.latest_photo && (
                <div className="dash-photo-box">
                  <p className="dash-photo-label">Latest</p>
                  <img src={`${API_BASE}${dash.latest_photo.url}`} alt="Latest progress" />
                  <span className="dash-photo-date">{dash.latest_photo.created_at?.slice(0, 10)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== Quick Stats Row ===== */}
        <div className="dash-stats-row">
          <div className="dash-stat-card" data-testid="stat-goal">
            <Target size={20} className="dash-stat-icon" />
            <div><p className="dash-stat-label">Goal</p><p className="dash-stat-value">{goalLabels[p.goal] || p.goal || '—'}</p></div>
          </div>
          <div className="dash-stat-card" data-testid="stat-weight">
            <Scale size={20} className="dash-stat-icon" />
            <div><p className="dash-stat-label">Current Weight</p><p className="dash-stat-value">{p.current_weight ? `${p.current_weight}kg` : '—'}</p></div>
          </div>
          <div className="dash-stat-card" data-testid="stat-workouts-week">
            <Activity size={20} className="dash-stat-icon" />
            <div><p className="dash-stat-label">Workouts This Week</p><p className="dash-stat-value">{dash?.workouts_this_week || 0} / {p.training_frequency || '?'}</p></div>
          </div>
          <div className="dash-stat-card" data-testid="stat-avg-cals">
            <Flame size={20} className="dash-stat-icon" />
            <div><p className="dash-stat-label">Avg Cals This Week</p><p className="dash-stat-value">{dash?.avg_calories_this_week || '—'} {p.daily_calorie_target ? `/ ${p.daily_calorie_target}` : ''}</p></div>
          </div>
        </div>

        {/* ===== Last Session ===== */}
        <div className="dash-card" data-testid="last-session">
          <h3 className="dash-card-title"><Clock size={18} /> Last Session</h3>
          {dash?.last_workout ? (
            <div className="dash-last-session">
              <span className="portal-badge">{dash.last_workout.workout_type}</span>
              <strong>{dash.last_workout.description}</strong>
              <span className="dash-muted">{dash.last_workout.duration_minutes} mins — {dash.last_workout.date}</span>
            </div>
          ) : <p className="portal-muted">No workouts logged yet.</p>}
        </div>

        {/* ===== Weight Trend ===== */}
        <div className="dash-card" data-testid="weight-chart">
          <div className="dash-card-header">
            <h3 className="dash-card-title"><TrendingDown size={18} /> Weight Trend</h3>
            <button onClick={() => setShowWeightForm(!showWeightForm)} className="portal-btn-sm"><Plus size={14} /> Log Weight</button>
          </div>
          {showWeightForm && (
            <form onSubmit={submitWeight} className="dash-inline-form">
              <input type="date" value={weightForm.date} onChange={e => setWeightForm(f => ({ ...f, date: e.target.value }))} required />
              <input type="number" step="0.1" placeholder="Weight (kg)" value={weightForm.weight} onChange={e => setWeightForm(f => ({ ...f, weight: e.target.value }))} required />
              <button type="submit" className="portal-btn-primary">Save</button>
            </form>
          )}
          {weightData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={weightData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E5E9" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6B7280' }} />
                <YAxis domain={['auto', 'auto']} tick={{ fontSize: 12, fill: '#6B7280' }} />
                {p.target_weight && <ReferenceLine y={p.target_weight} stroke="#b1d44c" strokeDasharray="5 5" label={{ value: `Target: ${p.target_weight}kg`, position: 'right', fontSize: 11, fill: '#b1d44c' }} />}
                <Tooltip />
                <Line type="monotone" dataKey="weight" stroke="#111" strokeWidth={2} dot={{ fill: '#b1d44c', r: 4 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="portal-muted">Log your weight to see the trend.</p>}
        </div>

        {/* ===== Calorie Tracking ===== */}
        <div className="dash-card" data-testid="calorie-chart">
          <div className="dash-card-header">
            <h3 className="dash-card-title"><Flame size={18} /> Calorie Tracking</h3>
            <button onClick={() => setShowCalForm(!showCalForm)} className="portal-btn-sm"><Plus size={14} /> Log Calories</button>
          </div>
          {showCalForm && (
            <form onSubmit={submitCalories} className="dash-inline-form">
              <input type="date" value={calForm.date} onChange={e => setCalForm(f => ({ ...f, date: e.target.value }))} required />
              <input type="number" placeholder="Calories" value={calForm.total_calories} onChange={e => setCalForm(f => ({ ...f, total_calories: e.target.value }))} required />
              <input type="number" placeholder="Protein (g)" value={calForm.protein} onChange={e => setCalForm(f => ({ ...f, protein: e.target.value }))} />
              <input type="number" placeholder="Carbs (g)" value={calForm.carbs} onChange={e => setCalForm(f => ({ ...f, carbs: e.target.value }))} />
              <input type="number" placeholder="Fats (g)" value={calForm.fats} onChange={e => setCalForm(f => ({ ...f, fats: e.target.value }))} />
              <button type="submit" className="portal-btn-primary">Save</button>
            </form>
          )}
          {calData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={calData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E5E9" />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6B7280' }} />
                <YAxis tick={{ fontSize: 12, fill: '#6B7280' }} />
                {p.daily_calorie_target && <ReferenceLine y={p.daily_calorie_target} stroke="#b1d44c" strokeDasharray="5 5" label={{ value: `Target: ${p.daily_calorie_target}`, position: 'right', fontSize: 11, fill: '#b1d44c' }} />}
                <Tooltip />
                <Bar dataKey="cals" fill="#111" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="portal-muted">Log your calories to see the chart.</p>}
        </div>

        {/* ===== Workouts Per Week ===== */}
        <div className="dash-card" data-testid="workouts-chart">
          <div className="dash-card-header">
            <h3 className="dash-card-title"><TrendingUp size={18} /> Workouts Per Week</h3>
            <button onClick={() => setShowWorkoutForm(!showWorkoutForm)} className="portal-btn-sm"><Plus size={14} /> Log Workout</button>
          </div>
          {showWorkoutForm && (
            <form onSubmit={submitWorkout} className="dash-inline-form">
              <input type="date" value={workoutForm.date} onChange={e => setWorkoutForm(f => ({ ...f, date: e.target.value }))} required />
              <select value={workoutForm.workout_type} onChange={e => setWorkoutForm(f => ({ ...f, workout_type: e.target.value }))}>
                <option value="gym">Gym</option><option value="cardio">Cardio</option><option value="other">Other</option>
              </select>
              <input type="number" placeholder="Duration (mins)" value={workoutForm.duration_minutes} onChange={e => setWorkoutForm(f => ({ ...f, duration_minutes: e.target.value }))} required />
              <input placeholder="Description" value={workoutForm.description} onChange={e => setWorkoutForm(f => ({ ...f, description: e.target.value }))} required />
              <button type="submit" className="portal-btn-primary">Save</button>
            </form>
          )}
          {workoutWeekData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={workoutWeekData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E5E9" />
                <XAxis dataKey="week" tick={{ fontSize: 12, fill: '#6B7280' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
                {p.training_frequency && <ReferenceLine y={p.training_frequency} stroke="#b1d44c" strokeDasharray="5 5" label={{ value: `Target: ${p.training_frequency}`, position: 'right', fontSize: 11, fill: '#b1d44c' }} />}
                <Tooltip />
                <Bar dataKey="sessions" fill="#b1d44c" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="portal-muted">Log workouts to see weekly trends.</p>}
        </div>

        {/* ===== Progress Photos ===== */}
        <div className="dash-card dash-card-wide" data-testid="progress-photos">
          <div className="dash-card-header">
            <h3 className="dash-card-title"><Camera size={18} /> Progress Photos ({dash?.total_photos || 0})</h3>
            <div>
              <input type="file" accept="image/*" ref={fileInputRef} onChange={uploadPhoto} style={{ display: 'none' }} />
              <button onClick={() => fileInputRef.current?.click()} className="portal-btn-sm" disabled={uploading}>
                {uploading ? <Loader2 size={14} className="portal-spin" /> : <Upload size={14} />} Upload Photo
              </button>
            </div>
          </div>
          {(dash?.all_photos || []).length > 0 ? (
            <div className="dash-photo-grid">
              {dash.all_photos.map((ph, i) => (
                <div key={i} className="dash-photo-thumb">
                  <img src={`${API_BASE}${ph.url}`} alt={`Progress ${i + 1}`} />
                  <span>{ph.created_at?.slice(0, 10)}</span>
                </div>
              ))}
            </div>
          ) : <p className="portal-muted">Upload your first progress photo to start tracking visually.</p>}
        </div>

        {/* ===== AI Advice ===== */}
        <div className="dash-card dash-card-wide" data-testid="ai-advice-card">
          <div className="dash-card-header">
            <h3 className="dash-card-title"><Brain size={18} /> AI Coach</h3>
            <button onClick={getAdvice} disabled={adviceLoading} className="portal-btn-sm">
              {adviceLoading ? <Loader2 size={14} className="portal-spin" /> : <Brain size={14} />} {adviceLoading ? 'Analysing...' : 'Get Advice'}
            </button>
          </div>
          {advice ? (
            <div className="portal-advice-card">
              {advice.split('\n').map((p, i) => p.trim() ? <p key={i}>{p}</p> : null)}
            </div>
          ) : <p className="portal-muted">Get personalised AI advice based on your logged data.</p>}
        </div>

      </div>
    </div>
  );
};

export default ClientDashboard;
